import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { BundleFilterState } from "../ui/bundle-grouped-filter-bar";
import { BundleType } from "@/pages/bundles/types/grouped-bundle";

// Bundle-page view filters, resolved with a precedence chain:
//   1. URL query params (shareable / reload-stable, highest priority)
//   2. localStorage (per-browser preference)
//   3. defaults
// Changing a filter writes to BOTH the URL and localStorage, so a shared link
// reproduces the exact view and a fresh visit still restores the last-used one.

const STORAGE_KEY = "bundles.filters";
const FILTER_KEYS = [
  "types",
  "productIds",
  "productStatus",
  "productTypes",
] as const;

const DEFAULTS: BundleFilterState = {
  types: [],
  productIds: [],
  productStatus: [],
  productTypes: [],
};

const parseList = (sp: URLSearchParams, key: string): string[] => {
  const raw = sp.get(key);
  return raw == null
    ? []
    : raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
};

export function useBundleFilters(): [
  BundleFilterState,
  (next: BundleFilterState) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stored, setStored] = useLocalStorage<BundleFilterState>(
    STORAGE_KEY,
    DEFAULTS,
  );

  // URL wins whenever it carries any filter param; otherwise fall back to the
  // stored preference (merged over defaults to tolerate an older stored shape).
  const urlHasFilters = FILTER_KEYS.some((k) => searchParams.has(k));

  const filters = useMemo<BundleFilterState>(() => {
    if (!urlHasFilters) return { ...DEFAULTS, ...stored };
    return {
      types: parseList(searchParams, "types") as BundleType[],
      productIds: parseList(searchParams, "productIds"),
      productStatus: parseList(searchParams, "productStatus"),
      productTypes: parseList(searchParams, "productTypes"),
    };
  }, [searchParams, stored, urlHasFilters]);

  const setFilters = useCallback(
    (next: BundleFilterState) => {
      setStored(next);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          const apply = (key: string, arr: string[]) =>
            arr.length ? p.set(key, arr.join(",")) : p.delete(key);
          apply("types", next.types);
          apply("productIds", next.productIds);
          apply("productStatus", next.productStatus);
          apply("productTypes", next.productTypes);
          return p;
        },
        { replace: true },
      );
    },
    [setStored, setSearchParams],
  );

  return [filters, setFilters];
}
